import { api } from "@shared/api/client";
import type {
  AnomalyState,
  Comment,
  ComponentHierarchy,
  Kks,
  KksSearchResponse,
  Pid,
  PidStatus,
  PowerPlant,
  SpofRegisterImport,
  SpofRegisterPreview,
  System,
} from "@shared/types/domain";

// Power Plants
export const getPowerPlants = () =>
  api.get<PowerPlant[]>("/power-plants").then((r) => r.data);

export const createPowerPlant = (name: string) =>
  api.post<PowerPlant>("/power-plants", { name }).then((r) => r.data);

/** Static facts of a plant; a null field clears the stored value. */
export interface PowerPlantBody {
  name: string;
  latitude: number | null;
  longitude: number | null;
  configuration: string | null;
  technology: string | null;
}

export const updatePowerPlant = (id: string, body: PowerPlantBody) =>
  api.put<PowerPlant>(`/power-plants/${id}`, body).then((r) => r.data);

export const deletePowerPlant = (id: string) =>
  api.delete(`/power-plants/${id}`);

export const uploadFunctionalLocations = (id: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/power-plants/${id}/functional-locations`, form);
};

export const previewSpofRegister = (id: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post<SpofRegisterPreview>(
      `/power-plants/${id}/spof-register/preview`,
      form,
    )
    .then((r) => r.data);
};

export const uploadSpofRegister = (
  id: string,
  file: File,
  links: Record<string, string[]>,
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("links", JSON.stringify(links));
  return api
    .post<SpofRegisterImport>(`/power-plants/${id}/spof-register`, form)
    .then((r) => r.data);
};

// Systems
export const getSystems = (powerPlantId: string) =>
  api
    .get<System[]>(`/power-plants/${powerPlantId}/systems`)
    .then((r) => r.data);

export const createSystem = (
  powerPlantId: string,
  name: string,
  systemType: string,
  kksPrefixes: string[] = [],
) =>
  api
    .post<System>(`/power-plants/${powerPlantId}/systems`, {
      name,
      system_type: systemType,
      kks_prefixes: kksPrefixes,
    })
    .then((r) => r.data);

export const updateSystem = (
  powerPlantId: string,
  systemId: string,
  name: string,
  kksPrefixes: string[],
) =>
  api
    .put<System>(`/power-plants/${powerPlantId}/systems/${systemId}`, {
      name,
      kks_prefixes: kksPrefixes,
    })
    .then((r) => r.data);

export const deleteSystem = (powerPlantId: string, systemId: string) =>
  api.delete(`/power-plants/${powerPlantId}/systems/${systemId}`);

// PIDs
export const getPids = (powerPlantId: string, systemId: string) =>
  api
    .get<Pid[]>(`/power-plants/${powerPlantId}/systems/${systemId}/pids`)
    .then((r) => r.data);

export const uploadPids = (
  powerPlantId: string,
  systemId: string,
  files: File[],
) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  return api
    .post<Pid[]>(`/power-plants/${powerPlantId}/systems/${systemId}/pids`, form)
    .then((r) => r.data);
};

export const deletePid = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
) =>
  api.delete(`/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}`);

export const renamePid = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  filename: string,
) =>
  api
    .patch<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/filename`,
      { filename },
    )
    .then((r) => r.data);

export const getPid = (powerPlantId: string, systemId: string, pidId: string) =>
  api
    .get<Pid>(`/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}`)
    .then((r) => r.data);

export const getPidPdfUrl = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  removeInk = false,
) =>
  `${import.meta.env.VITE_BE_BASE_URL}pid-kks-extractor/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/pdf${removeInk ? "?remove_ink=true" : ""}`;

export const updateKks = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  kks: Kks[],
) =>
  api
    .put<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/kks`,
      kks,
    )
    .then((r) => r.data);

export const updatePidStatus = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  status: PidStatus,
) =>
  api
    .patch<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/status`,
      { status },
    )
    .then((r) => r.data);

export const updatePidSubSystem = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  sub_system: string,
) =>
  api
    .patch<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/sub-system`,
      { sub_system },
    )
    .then((r) => r.data);

export const lockPid = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  locked: boolean,
) =>
  api
    .patch<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/lock`,
      { locked },
    )
    .then((r) => r.data);

export const lockAllPids = (
  powerPlantId: string,
  systemId: string,
  locked: boolean,
) =>
  api
    .patch<
      Pid[]
    >(`/power-plants/${powerPlantId}/systems/${systemId}/pids/lock-all`, { locked })
    .then((r) => r.data);

export const lockRequiredSpofPid = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  locked: boolean,
) =>
  api
    .patch<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/required-spof-lock`,
      { locked },
    )
    .then((r) => r.data);

export const lockAllRequiredSpofPids = (
  powerPlantId: string,
  systemId: string,
  locked: boolean,
) =>
  api
    .patch<
      Pid[]
    >(`/power-plants/${powerPlantId}/systems/${systemId}/pids/lock-all-required-spof`, { locked })
    .then((r) => r.data);

export const retryPid = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
) =>
  api
    .post<Pid>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/retry`,
    )
    .then((r) => r.data);

// Component Hierarchy
export const getComponentHierarchy = () =>
  api.get<ComponentHierarchy>("/component-hierarchy").then((r) => r.data);

// Component Hierarchy CRUD
export const createHierarchySystem = (name: string) =>
  api.post("/component-hierarchy/systems", { name }).then((r) => r.data);

export const renameHierarchySystem = (old_name: string, new_name: string) =>
  api
    .put("/component-hierarchy/systems", { old_name, new_name })
    .then((r) => r.data);

export const deleteHierarchySystem = (systemName: string) =>
  api.delete(`/component-hierarchy/systems/${encodeURIComponent(systemName)}`);

export const createHierarchySubSystem = (system: string, name: string) =>
  api
    .post("/component-hierarchy/sub-systems", { system, name })
    .then((r) => r.data);

export const renameHierarchySubSystem = (
  system: string,
  old_name: string,
  new_name: string,
) =>
  api
    .put("/component-hierarchy/sub-systems", { system, old_name, new_name })
    .then((r) => r.data);

export const deleteHierarchySubSystem = (
  systemName: string,
  subSystemName: string,
) =>
  api.delete(
    `/component-hierarchy/sub-systems/${encodeURIComponent(systemName)}/${encodeURIComponent(subSystemName)}`,
  );

export const createHierarchyComponent = (
  system: string,
  sub_system: string,
  name: string,
) =>
  api
    .post("/component-hierarchy/components", { system, sub_system, name })
    .then((r) => r.data);

export const renameHierarchyComponent = (
  system: string,
  sub_system: string,
  old_name: string,
  new_name: string,
) =>
  api
    .put("/component-hierarchy/components", {
      system,
      sub_system,
      old_name,
      new_name,
    })
    .then((r) => r.data);

export const deleteHierarchyComponent = (
  system: string,
  sub_system: string,
  name: string,
) =>
  api.delete("/component-hierarchy/components", {
    data: { system, sub_system, name },
  });

// Exports

export const exportAssetMappingByPlant = (powerPlantId: string) =>
  api
    .post(`/power-plants/${powerPlantId}/export/asset-mapping`)
    .then((r) => r.data);

export const exportAssetMappingBySystem = (
  powerPlantId: string,
  systemId: string,
) =>
  api
    .post(
      `/power-plants/${powerPlantId}/systems/${systemId}/export/asset-mapping`,
    )
    .then((r) => r.data);

export const exportTotalEvidencePackByPlant = (powerPlantId: string) =>
  api
    .post(`/power-plants/${powerPlantId}/export/total-evidence-pack`)
    .then((r) => r.data);

export const exportTotalEvidencePackBySystem = (
  powerPlantId: string,
  systemId: string,
) =>
  api
    .post(
      `/power-plants/${powerPlantId}/systems/${systemId}/export/total-evidence-pack`,
    )
    .then((r) => r.data);

export const exportRequiredEvidencePackByPlant = (powerPlantId: string) =>
  api
    .post(`/power-plants/${powerPlantId}/export/required-evidence-pack`)
    .then((r) => r.data);

export const exportRequiredEvidencePackBySystem = (
  powerPlantId: string,
  systemId: string,
) =>
  api
    .post(
      `/power-plants/${powerPlantId}/systems/${systemId}/export/required-evidence-pack`,
    )
    .then((r) => r.data);

export const downloadPidPdf = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
): Promise<Blob> =>
  api
    .get(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/pdf`,
      { responseType: "blob" },
    )
    .then((r) => r.data);

// Comments
export const getComments = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
) =>
  api
    .get<
      Comment[]
    >(`/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/comments`)
    .then((r) => r.data);

export const addComment = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  text: string,
) =>
  api
    .post<Comment>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/comments`,
      { text },
    )
    .then((r) => r.data);

export const updateComment = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  commentId: string,
  text: string,
) =>
  api
    .put<Comment>(
      `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/comments/${commentId}`,
      { text },
    )
    .then((r) => r.data);

export const deleteComment = (
  powerPlantId: string,
  systemId: string,
  pidId: string,
  commentId: string,
) =>
  api.delete(
    `/power-plants/${powerPlantId}/systems/${systemId}/pids/${pidId}/comments/${commentId}`,
  );

// Anomalies
export const getAnomalies = () =>
  api.get<AnomalyState>("/anomalies").then((r) => r.data);

export const refreshAnomalies = () =>
  api.post<{ dispatched: boolean }>("/anomalies/refresh").then((r) => r.data);

// KKS Search
export const searchKks = (q: string) =>
  api.get<KksSearchResponse>("/search", { params: { q } }).then((r) => r.data);
